import { guards } from '@shared/helpers/typeguards.helpers';
import {
  removeNumberFormatting,
  validateExponentNumberString,
  validatePlainNumberString,
} from '@shared/helpers/math.helpers';

/**
 * Parse a typed number and reject it when it sits outside min/max.
 * @param value - Raw input text
 * @param options - Bounds and an optional transform applied before the check
 */
export const parseCurrentNumberValue = (
  value: string,
  options: {
    min?: number;
    max?: number;
    valueTransform?: (v: number) => number;
  },
): Error | number | undefined => {
  const { min, max, valueTransform = (v) => v } = options;
  const formattedValue = removeNumberFormatting(value);
  const isEmpty = guards.is.emptyString(formattedValue);
  if (isEmpty) return undefined;

  const isPlainNumber = validatePlainNumberString(formattedValue);
  const isExponentNumber = validateExponentNumberString(formattedValue);
  if (!isPlainNumber && !isExponentNumber) return new Error('Invalid number');
  const parsedValue = parseFloat(formattedValue);
  if (!Number.isFinite(parsedValue)) return new Error('Could not parse number');
  const correctedValue = valueTransform(parsedValue);
  if (guards.is.defined(min) && correctedValue < min)
    return new Error(`Value below minimum. Min: ${min}`);
  if (guards.is.defined(max) && correctedValue > max)
    return new Error(`Value above maximum. Max: ${max}`);

  return correctedValue;
};

const controlledValueError = (
  value: number | undefined,
  min: number | undefined,
  max: number | undefined,
): string | undefined => {
  if (guards.is.undefined(value)) return undefined;
  const result = parseCurrentNumberValue(`${value}`, { min, max });
  return guards.is.error(result) ? result.message : undefined;
};

/**
 * Tooltip text for a number field.
 * A saved value outside min/max is shown when the field is idle. A typed
 * error waits until submit. {@link errorMessage} covers an expression that is
 * outside the bounds while the displayed number is not.
 */
export const unitNumberInputErrorTooltip = (options: {
  value?: number;
  min?: number;
  max?: number;
  isFocused: boolean;
  showValidationError: boolean;
  submittedError?: string;
  errorMessage?: string;
}): string | undefined => {
  const controlled = options.isFocused
    ? undefined
    : controlledValueError(options.value, options.min, options.max);
  return (
    (options.showValidationError ? options.submittedError : undefined) ??
    controlled ??
    options.errorMessage
  );
};
